import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../styles/smrttasks.css";
import SMRTTasksAddTaskModal, {
  SMRTTaskFormData,
  SMRTRecurrence,
} from "../components/SMRTTasksAddTaskModal";
import SMRTTasksRejectModal from "../components/SMRTTasksRejectModal";
import SMRTTasksConfirmModal from "../components/SMRTTasksConfirmModal";

type Status = "Open" | "Complete" | "Rejected";
type TabKey = "mine" | "waiting" | "done";
type SortColumn =
  | "title"
  | "taskType"
  | "assignedBy"
  | "assignedTo"
  | "org"
  | "date"
  | "started"
  | "completed";
type SortDirection = "asc" | "desc";
type TabSort = { column: SortColumn; direction: SortDirection };

interface TaskRemark {
  id: string;
  text: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  system?: boolean;
}

interface Task {
  id: string;
  orgID: string;
  orgLabel?: string;
  title: string;
  description?: string;
  taskTypeId: string;
  taskTypeName?: string;
  assignedUserIds: string[];
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  dueDate?: string;
  status: Status;
  isPrivate: boolean;
  recurrence?: SMRTRecurrence | null;
  showDaysBeforeDue?: number;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectedReason?: string;
  source?: "crmtix";
  remarks?: TaskRemark[];
  visibleAfter?: string;
  recurrenceParentId?: string;
}

interface TaskType {
  id: string;
  name: string;
}

interface OrgUser {
  user_id: string;
  name?: string;
  email: string;
}

const TASKS_API = `${process.env.REACT_APP_BACKEND_URL}/smrt-tasks`;
const TYPES_API = `${process.env.REACT_APP_BACKEND_URL}/smrt-task-types`;
const USERS_API = `${process.env.REACT_APP_BACKEND_URL}/approved_users`;

// CRMTIX-created tasks prefix descriptions with `[cheque:<id>]` so the
// CRMTIX page can join tasks back to their cheque. Strip it here so the
// marker doesn't bleed into the user-facing description text.
function stripCrmtixMarker(description?: string): string | undefined {
  if (!description) return description;
  return description.replace(/^\s*\[cheque:[^\]]+\]\s*/, "");
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const RECURRENCE_DAY_NAMES = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

// Renders a SMRTRecurrence as a human-readable label for the expanded
// view. Mirrors the schema↔UI mapping in SMRTTasksAddTaskModal: a daily
// recurrence with everyXDays=1 surfaces as "Daily" since that's the
// option the user actually picked.
function describeRecurrence(rec?: SMRTRecurrence | null): string | null {
  if (!rec || rec.type === "none") return null;
  let main = "";
  switch (rec.type) {
    case "daily": {
      const x = rec.everyXDays || 1;
      main = x === 1 ? "Daily" : `Every ${x} days`;
      break;
    }
    case "weekdays":
      main = "Every weekday (Mon–Fri)";
      break;
    case "selected":
      main =
        "Every " +
        (rec.daysOfWeek || [])
          .slice()
          .sort((a, b) => a - b)
          .map((d) => RECURRENCE_DAY_NAMES[d] || "?")
          .join(", ");
      break;
    case "weekly": {
      const w = rec.everyXWeeks || 1;
      main = w === 1 ? "Weekly" : `Every ${w} weeks`;
      break;
    }
    case "monthly":
      main = `Monthly on day ${rec.dayOfMonth || 1}`;
      break;
    case "quarterly":
      main = `Quarterly on day ${rec.dayOfMonth || 1}`;
      break;
    default:
      main = rec.type;
  }
  if (rec.endDate) {
    return `${main}, until ${formatDate(rec.endDate)}`;
  }
  return main;
}

function formatDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SMRTTasks() {
  const [activeTab, setActiveTab] = useState<TabKey>("mine");
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [waiting, setWaiting] = useState<Task[]>([]);
  const [completed, setCompleted] = useState<Task[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [currentUserID, setCurrentUserID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Inline remark composer — only one row can have an active textarea at a
  // time. Triggered by the row's Remark action; submitting or cancelling
  // clears the active id.
  const [remarkActiveId, setRemarkActiveId] = useState<string | null>(null);
  const [remarkText, setRemarkText] = useState("");
  const [remarkPosting, setRemarkPosting] = useState(false);
  const [remarkError, setRemarkError] = useState<string | null>(null);
  const remarkTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Mobile-only ⋮ menu — at most one row's secondary-action menu is open at
  // a time. Tapping the same trigger toggles it; tapping a different row's
  // trigger swaps; tapping anywhere outside .smrttasks-task-actions closes.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = useCallback((taskId: string) => {
    setOpenMenuId((prev) => (prev === taskId ? null : taskId));
  }, []);

  // Close the open menu when the user taps anywhere outside any row's
  // action area. Bound only while a menu is open so we don't run a global
  // listener for free.
  useEffect(() => {
    if (!openMenuId) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest(".smrttasks-task-actions")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openMenuId]);

  const [sortByTab, setSortByTab] = useState<Record<TabKey, TabSort>>({
    mine: { column: "date", direction: "asc" },
    waiting: { column: "date", direction: "asc" },
    done: { column: "completed", direction: "desc" },
  });

  const toggleSort = (column: SortColumn) => {
    setSortByTab((prev) => {
      const cur = prev[activeTab];
      const next: TabSort =
        cur.column === column
          ? { column, direction: cur.direction === "asc" ? "desc" : "asc" }
          : { column, direction: "asc" };
      return { ...prev, [activeTab]: next };
    });
  };

  // Persist the user's view preferences (active tab + per-tab sort) to
  // localStorage so they survive navigation, browser reload, and
  // logout/login. Keyed per user so two accounts on the same browser
  // don't share preferences. `hydratedRef` gates the save effect: until
  // we've had a chance to load saved values for the current user, we
  // must NOT write our component defaults to disk and clobber them.
  const PREFS_KEY_PREFIX = "smrtTasks.prefs.v1:";
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!currentUserID) return;
    try {
      const raw = localStorage.getItem(PREFS_KEY_PREFIX + currentUserID);
      if (raw) {
        const saved = JSON.parse(raw) as {
          activeTab?: TabKey;
          sortByTab?: Partial<Record<TabKey, TabSort>>;
        };
        if (
          saved.activeTab === "mine" ||
          saved.activeTab === "waiting" ||
          saved.activeTab === "done"
        ) {
          setActiveTab(saved.activeTab);
        }
        if (saved.sortByTab && typeof saved.sortByTab === "object") {
          setSortByTab((prev) => ({ ...prev, ...saved.sortByTab }));
        }
      }
    } catch {
      // Corrupt JSON or no localStorage — fall back to defaults silently.
    }
    hydratedRef.current = true;
  }, [currentUserID]);

  useEffect(() => {
    if (!currentUserID || !hydratedRef.current) return;
    try {
      localStorage.setItem(
        PREFS_KEY_PREFIX + currentUserID,
        JSON.stringify({ activeTab, sortByTab }),
      );
    } catch {
      // Quota / private-mode failures are non-fatal for the page.
    }
  }, [currentUserID, activeTab, sortByTab]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Task | null>(null);

  const [confirmState, setConfirmState] = useState<{
    type: "complete" | "delete" | "reopen";
    task: Task;
  } | null>(null);
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Per-task in-flight set for the row Complete button. Set on request start,
  // cleared on settle (success or failure) so the button restores either way.
  // Set rather than single ID so back-to-back row completes each track
  // independently.
  const [pendingCompleteIds, setPendingCompleteIds] = useState<Set<string>>(
    () => new Set(),
  );
  const markPending = useCallback((id: string) => {
    setPendingCompleteIds((s) => {
      const n = new Set(s);
      n.add(id);
      return n;
    });
  }, []);
  const clearPending = useCallback((id: string) => {
    setPendingCompleteIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }, []);

  const taskTypeName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of taskTypes) map[t.id] = t.name;
    return (id: string) => map[id] || "Unknown";
  }, [taskTypes]);

  // Stable initialData reference for the add/edit modal. Without
  // useMemo, this object would be rebuilt on every parent render — and
  // the background silent refresh re-renders the parent every 15s.
  // The modal's init effect watches `initialData` by reference, so a
  // fresh object every render = form gets reset out from under the
  // user mid-edit. Memoising on `editingTask` pins the reference for
  // the lifetime of a given edit session.
  const editTaskInitialData = useMemo(() => {
    if (!editingTask) return undefined;
    return {
      title: editingTask.title,
      description: editingTask.description,
      taskTypeId: editingTask.taskTypeId,
      assignedUserIds: editingTask.assignedUserIds,
      dueDate: editingTask.dueDate,
      isPrivate: editingTask.isPrivate,
      recurrence: editingTask.recurrence ?? null,
      showDaysBeforeDue: editingTask.showDaysBeforeDue,
      recurrenceParentId: editingTask.recurrenceParentId,
    };
  }, [editingTask]);

  const userName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.user_id] = u.name || u.email;
    return (id: string) => map[id] || id;
  }, [users]);

  // `silent: true` skips the loading-flag toggle so post-action refetches don't
  // unmount the task list (no "Loading..." flash, scroll/tab/expanded state
  // preserved). Initial mount uses the default loud refetch to render the spinner.
  const loadAll = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [tasksRes, typesRes, usersRes] = await Promise.all([
        fetch(TASKS_API, { credentials: "include" }),
        fetch(TYPES_API, { credentials: "include" }),
        fetch(USERS_API, { credentials: "include" }),
      ]);
      if (!tasksRes.ok)
        throw new Error(`Failed to load tasks (${tasksRes.status})`);
      const taskData = await tasksRes.json();
      setMyTasks(Array.isArray(taskData?.myTasks) ? taskData.myTasks : []);
      setWaiting(Array.isArray(taskData?.waiting) ? taskData.waiting : []);
      setCompleted(
        Array.isArray(taskData?.completed) ? taskData.completed : [],
      );
      setCurrentUserID(taskData?.currentUserID || null);

      if (typesRes.ok) {
        const td = await typesRes.json();
        setTaskTypes(Array.isArray(td?.taskTypes) ? td.taskTypes : []);
      }
      if (usersRes.ok) {
        const ud = await usersRes.json();
        setUsers(Array.isArray(ud) ? ud : []);
      }
    } catch (err: unknown) {
      setError((err as Error).message || "Failed to load");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Background refresh so a recipient sees a newly-assigned task (or a
  // new comment / status change) without manually reloading. Silent refetch
  // preserves loading state and expanded-row state. Three signals trigger
  // a refresh: a 15s polling tick, the tab regaining focus, and the
  // visibility state flipping to "visible" — the latter two make the
  // common "I switched back to this tab" path feel instant without
  // dropping the poll interval into single-digit seconds.
  useEffect(() => {
    const tick = () => loadAll({ silent: true });
    const id = setInterval(tick, 15 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", tick);
    };
  }, [loadAll]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openRemarkComposer = (task: Task) => {
    // Make sure the row is expanded so the textarea is visible. Focus runs
    // on the next tick via the useEffect below.
    setExpandedIds((prev) => {
      if (prev.has(task.id)) return prev;
      const next = new Set(prev);
      next.add(task.id);
      return next;
    });
    setRemarkActiveId(task.id);
    setRemarkText("");
    setRemarkError(null);
    setOpenMenuId(null);
  };

  const cancelRemark = () => {
    if (remarkPosting) return;
    setRemarkActiveId(null);
    setRemarkText("");
    setRemarkError(null);
  };

  const postRemark = async (task: Task) => {
    const text = remarkText.trim();
    if (!text) {
      setRemarkError("Remark cannot be empty");
      return;
    }
    setRemarkPosting(true);
    setRemarkError(null);
    try {
      const res = await fetch(
        `${TASKS_API}/${encodeURIComponent(task.id)}/remarks`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orgID: task.orgID, text }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed (${res.status})`);
      }
      setRemarkActiveId(null);
      setRemarkText("");
      await loadAll({ silent: true });
    } catch (err: unknown) {
      setRemarkError((err as Error).message || "Failed to add remark");
    } finally {
      setRemarkPosting(false);
    }
  };

  // Focus the composer the moment it becomes active, so the user can start
  // typing without a second click. Runs on the tick after expandedIds and
  // remarkActiveId both settle.
  useEffect(() => {
    if (!remarkActiveId) return;
    const t = setTimeout(() => remarkTextareaRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [remarkActiveId]);

  const openCreate = () => {
    setModalMode("create");
    setEditingTask(null);
    setModalOpen(true);
  };

  const openEdit = (task: Task) => {
    setModalMode("edit");
    setEditingTask(task);
    setModalOpen(true);
  };

  const togglePrivate = async (task: Task) => {
    try {
      const res = await fetch(
        `${TASKS_API}/${encodeURIComponent(task.id)}/private`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orgID: task.orgID,
            isPrivate: !task.isPrivate,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed (${res.status})`);
      }
      await loadAll({ silent: true });
    } catch (err: unknown) {
      setError((err as Error).message || "Failed to update privacy");
    }
  };

  const handleModalSubmit = async (payload: SMRTTaskFormData) => {
    // Privacy is managed via the per-row lock button; preserve the existing
    // value when editing, default to public on create.
    const body = {
      ...payload,
      isPrivate: editingTask ? !!editingTask.isPrivate : false,
    };
    if (modalMode === "create") {
      const res = await fetch(TASKS_API, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Failed to create (${res.status})`);
      }
    } else if (editingTask) {
      const res = await fetch(
        `${TASKS_API}/${encodeURIComponent(editingTask.id)}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, orgID: editingTask.orgID }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Failed to update (${res.status})`);
      }
    }
    setModalOpen(false);
    setEditingTask(null);
    await loadAll({ silent: true });
  };

  const completeTask = async (task: Task) => {
    // CRMTIX-sourced tasks gate behind a confirmation modal. The confirmed
    // path runs in runConfirm and dispatches "smrt-tasks-changed" so CRMTIX
    // re-fetches and the next workflow action surfaces in the same tab.
    if (task.source === "crmtix") {
      setConfirmError(null);
      setConfirmSaving(false);
      setConfirmState({ type: "complete", task });
      return;
    }
    markPending(task.id);
    try {
      const res = await fetch(
        `${TASKS_API}/${encodeURIComponent(task.id)}/complete`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orgID: task.orgID }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed (${res.status})`);
      }
      await loadAll({ silent: true });
      // Notify in-tab listeners (CRMTIX) so legacy cheque tasks without a
      // source="crmtix" tag still trigger a workflow-state refresh. Mirrors
      // the dispatch in the modal-confirm path so coverage is symmetric.
      window.dispatchEvent(new Event("smrt-tasks-changed"));
    } catch (err: unknown) {
      setError((err as Error).message || "Failed to complete task");
    } finally {
      clearPending(task.id);
    }
  };

  const askDelete = (task: Task) => {
    setConfirmError(null);
    setConfirmSaving(false);
    setConfirmState({ type: "delete", task });
  };

  const askReopen = (task: Task) => {
    setConfirmError(null);
    setConfirmSaving(false);
    setConfirmState({ type: "reopen", task });
  };

  const closeConfirm = () => {
    if (confirmSaving) return;
    setConfirmState(null);
    setConfirmError(null);
  };

  const runConfirm = async () => {
    if (!confirmState) return;
    setConfirmSaving(true);
    setConfirmError(null);
    try {
      if (confirmState.type === "complete") {
        const taskId = confirmState.task.id;
        markPending(taskId);
        try {
          const res = await fetch(
            `${TASKS_API}/${encodeURIComponent(taskId)}/complete`,
            {
              method: "PUT",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ orgID: confirmState.task.orgID }),
            },
          );
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error || `Failed (${res.status})`);
          }
          // Notify other in-tab listeners (CRMTIX) that a CRMTIX task just
          // completed so their workflow gates re-evaluate without relying on
          // window focus or remount.
          window.dispatchEvent(new Event("smrt-tasks-changed"));
        } finally {
          clearPending(taskId);
        }
      } else if (confirmState.type === "delete") {
        const res = await fetch(
          `${TASKS_API}/${encodeURIComponent(confirmState.task.id)}?orgID=${encodeURIComponent(confirmState.task.orgID)}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Failed (${res.status})`);
        }
      } else if (confirmState.type === "reopen") {
        const res = await fetch(
          `${TASKS_API}/${encodeURIComponent(confirmState.task.id)}/reopen`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orgID: confirmState.task.orgID }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Failed (${res.status})`);
        }
      }
      setConfirmState(null);
      setConfirmSaving(false);
      await loadAll({ silent: true });
    } catch (err: unknown) {
      setConfirmError((err as Error).message || "Action failed");
      setConfirmSaving(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    const res = await fetch(
      `${TASKS_API}/${encodeURIComponent(rejectTarget.id)}/reject`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, orgID: rejectTarget.orgID }),
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error || `Failed (${res.status})`);
    }
    setRejectTarget(null);
    await loadAll({ silent: true });
  };

  const renderAssignees = (task: Task) =>
    task.assignedUserIds.length === 0
      ? "—"
      : task.assignedUserIds.map((id) => userName(id)).join(", ");

  const rowClassForTab = (tab: TabKey) =>
    tab === "done"
      ? "smrttasks-task-row-done"
      : tab === "waiting"
        ? "smrttasks-task-row-waiting"
        : "smrttasks-task-row-mine";

  const headerClassForTab = (tab: TabKey) =>
    tab === "done"
      ? "smrttasks-task-header-done"
      : tab === "waiting"
        ? "smrttasks-task-header-waiting"
        : "smrttasks-task-header-mine";

  const renderTaskRow = (task: Task, section: TabKey) => {
    const isRejected = task.status === "Rejected";
    const isCreator = currentUserID !== null && task.createdBy === currentUserID;
    const isAssignee =
      currentUserID !== null && (task.assignedUserIds || []).includes(currentUserID);
    const isCRMTIX = task.source === "crmtix";
    const isCompletePending = pendingCompleteIds.has(task.id);
    const canTogglePrivate = isCreator && isAssignee && !isCRMTIX;
    // Edit: creator can change anything; assignees can change due date and
    // assigned users (server enforces the split). Either role gets the button.
    const canEdit = (isCreator || isAssignee) && !isCRMTIX;
    // Reopen: either role can recover an accidental completion without
    // escalating. Mirrors the relaxed PUT /reopen permission on the server.
    const canReopen =
      section === "done" &&
      (isCreator || isAssignee) &&
      task.status === "Complete" &&
      !isCRMTIX;
    const createdByLabel =
      task.source === "crmtix"
        ? "CRMTIX Cheques"
        : task.createdByName || userName(task.createdBy);
    const isExpanded = expandedIds.has(task.id);
    const isMenuOpen = openMenuId === task.id;

    let titleColorClass = "";
    if (section === "done") {
      if (isRejected) {
        titleColorClass = " smrttasks-task-title-rejected";
      } else if (
        task.status === "Complete" &&
        isCreator &&
        !!task.completedBy &&
        task.completedBy !== currentUserID
      ) {
        titleColorClass = " smrttasks-task-title-issued";
      }
    }

    const assigneesText = renderAssignees(task);

    const typeLabel = task.taskTypeName || taskTypeName(task.taskTypeId);
    const recurrenceLabel = describeRecurrence(task.recurrence);

    // Remark: creator or any assignee can comment on a task in any tab.
    // CRMTIX tasks are workflow-driven and don't accept user remarks.
    const canRemark = !isCRMTIX && (isCreator || isAssignee);

    // Whether this row has any secondary actions to surface in the ⋮ menu.
    // mine + waiting always do (reject / edit / delete are present
    // regardless of creator). done shows the trigger when remark or reopen
    // is available. CRMTIX tasks have no secondary actions at all
    // (View + Complete only). When false, the ⋮ trigger doesn't render
    // and the row stays icon-only on mobile.
    const hasSecondaryActions =
      !isCRMTIX && (section !== "done" || canReopen || canRemark);

    // Mobile-only: tapping the row body (not a button) toggles expand.
    // Width-gated so desktop behaviour stays identical. Cheap matchMedia
    // check on each click — no listener needed.
    const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!window.matchMedia("(max-width: 720px)").matches) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest("button")) return;
      // Interactions inside the remarks list or composer (tapping text,
      // focusing the textarea) should not collapse the row.
      if (target.closest(".smrttasks-task-remarks")) return;
      if (target.closest(".smrttasks-task-remark-composer")) return;
      toggleExpand(task.id);
    };

    // Mobile-only date line shown directly under the title. Desktop hides
    // this via CSS and reads the date from its dedicated grid cell below.
    const mobileDateLine =
      section === "done"
        ? formatDate(task.completedAt || task.rejectedAt) || ""
        : formatDate(task.dueDate) || "";

    return (
      <div
        key={task.id}
        className={`smrttasks-task-row ${rowClassForTab(section)}`}
        data-menu-open={isMenuOpen ? "true" : undefined}
        onClick={handleRowClick}
      >
        <div className="smrttasks-task-main">
          <div className={`smrttasks-task-title${titleColorClass}`}>
            {task.title}
          </div>

          {mobileDateLine && (
            <div className="smrttasks-task-mobile-due">
              {section === "done" ? "Completed" : "Due"}: {mobileDateLine}
            </div>
          )}

          {isExpanded && task.description && (
            <div className="smrttasks-task-description">
              {stripCrmtixMarker(task.description)}
            </div>
          )}

          {isExpanded && (
            <div className="smrttasks-task-details">
              <div>
                <strong>Assigned by:</strong> {createdByLabel}
              </div>
              <div>
                <strong>Assigned on:</strong> {formatDate(task.createdAt)}
              </div>
              <div>
                <strong>Task Type:</strong> {typeLabel}
              </div>
              {recurrenceLabel && (
                <div>
                  <strong>Repeat:</strong> {recurrenceLabel}
                </div>
              )}
            </div>
          )}

          {isExpanded &&
            section === "done" &&
            task.status === "Complete" &&
            task.completedBy && (
              <div className="smrttasks-task-completion">
                <strong>Completed by:</strong>{" "}
                {task.completedByName || userName(task.completedBy)}
                {task.completedAt && (
                  <>
                    {" "}
                    <strong>at:</strong> {formatDateTime(task.completedAt)}
                  </>
                )}
              </div>
            )}

          {isExpanded &&
            section === "done" &&
            isRejected &&
            task.rejectedReason && (
              <div className="smrttasks-task-rejection">
                <strong>Reason:</strong> {task.rejectedReason}
              </div>
            )}

          {isExpanded &&
            Array.isArray(task.remarks) &&
            task.remarks.length > 0 && (
              <div className="smrttasks-task-remarks">
                {task.remarks.map((r) => (
                  <div
                    key={r.id}
                    className={`smrttasks-task-remark${r.system ? " smrttasks-task-remark-system" : ""}`}
                  >
                    <div className="smrttasks-task-remark-text">{r.text}</div>
                    <div className="smrttasks-task-remark-meta">
                      {r.createdByName || userName(r.createdBy)} ·{" "}
                      {formatDateTime(r.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {isExpanded && remarkActiveId === task.id && (
            <div className="smrttasks-task-remark-composer">
              <textarea
                ref={remarkTextareaRef}
                className="smrttasks-textarea"
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Add a remark…"
                disabled={remarkPosting}
                onClick={(e) => e.stopPropagation()}
              />
              {remarkError && (
                <div className="smrttasks-task-remark-error">{remarkError}</div>
              )}
              <div className="smrttasks-task-remark-actions">
                <button
                  type="button"
                  className="smrttasks-btn-cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelRemark();
                  }}
                  disabled={remarkPosting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="smrttasks-btn-save"
                  onClick={(e) => {
                    e.stopPropagation();
                    postRemark(task);
                  }}
                  disabled={remarkPosting || !remarkText.trim()}
                >
                  {remarkPosting ? "Posting…" : "Post"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="smrttasks-task-cell" title={typeLabel}>
          {typeLabel}
        </div>

        {section === "mine" && (
          <>
            <div className="smrttasks-task-cell" title={createdByLabel}>
              {createdByLabel}
            </div>
            <div className="smrttasks-task-cell" title={task.orgLabel}>
              {task.orgLabel || "—"}
            </div>
            <div className="smrttasks-task-date">{formatDate(task.dueDate)}</div>
          </>
        )}

        {section === "waiting" && (
          <>
            <div className="smrttasks-task-cell" title={assigneesText}>
              {assigneesText}
            </div>
            <div className="smrttasks-task-cell" title={task.orgLabel}>
              {task.orgLabel || "—"}
            </div>
            <div className="smrttasks-task-date">{formatDate(task.dueDate)}</div>
          </>
        )}

        {section === "done" && (
          <>
            <div className="smrttasks-task-cell" title={createdByLabel}>
              {createdByLabel}
            </div>
            <div className="smrttasks-task-cell" title={assigneesText}>
              {assigneesText}
            </div>
            <div className="smrttasks-task-cell" title={task.orgLabel}>
              {task.orgLabel || "—"}
            </div>
            <div className="smrttasks-task-date">
              {formatDate(task.createdAt)}
            </div>
            <div className="smrttasks-task-date">
              {formatDate(task.completedAt || task.rejectedAt)}
            </div>
          </>
        )}

        <div className="smrttasks-task-actions">
          {/* Primary actions — always inline on every viewport. */}
          <button
            type="button"
            className="smrttasks-btn-icon smrttasks-btn-view"
            title={isExpanded ? "Hide description" : "Show description"}
            data-label={isExpanded ? "Hide" : "Expand"}
            onClick={() => toggleExpand(task.id)}
          >
            👁
          </button>

          {section === "mine" && (
            <button
              type="button"
              className="smrttasks-btn-icon smrttasks-btn-complete"
              title={isCompletePending ? "Completing…" : "Complete"}
              data-label={isCompletePending ? "Working…" : "Complete"}
              aria-busy={isCompletePending || undefined}
              disabled={isCompletePending}
              onClick={() => completeTask(task)}
            >
              {isCompletePending ? (
                <span className="smrttasks-btn-spinner" aria-hidden />
              ) : (
                "✓"
              )}
            </button>
          )}

          {/* Mobile-only ⋮ trigger — rendered BEFORE secondary actions so
              that when the menu opens (each secondary button wraps to a
              full-width row below), the trigger stays visually anchored
              alongside the primary icons on the first row. CSS-revealed
              at narrow widths only when there are secondary actions to
              expose. */}
          {hasSecondaryActions && (
            <button
              type="button"
              className="smrttasks-btn-icon smrttasks-btn-menu-trigger"
              title={isMenuOpen ? "Hide actions" : "More actions"}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu(task.id);
              }}
            >
              ⋮
            </button>
          )}

          {/* Secondary actions — inline on desktop, collapsed behind ⋮ on
              mobile via .smrttasks-btn-secondary-action + the row's
              data-menu-open attribute. */}
          {section === "mine" && !isCRMTIX && (
            <>
              {canEdit && (
                <button
                  type="button"
                  className="smrttasks-btn-icon smrttasks-btn-edit smrttasks-btn-secondary-action"
                  title={isCreator ? "Edit" : "Edit due date and assignees"}
                  data-label="Edit"
                  onClick={() => openEdit(task)}
                >
                  ✎
                </button>
              )}
              {canRemark && (
                <button
                  type="button"
                  className="smrttasks-btn-icon smrttasks-btn-remark smrttasks-btn-secondary-action"
                  title="Add a remark"
                  data-label="Remark"
                  onClick={() => openRemarkComposer(task)}
                >
                  💬
                </button>
              )}
              <button
                type="button"
                className="smrttasks-btn-icon smrttasks-btn-reject-icon smrttasks-btn-secondary-action"
                title="Reject"
                data-label="Reject"
                onClick={() => setRejectTarget(task)}
              >
                ✕
              </button>
              {isCreator && (
                <button
                  type="button"
                  className="smrttasks-btn-icon smrttasks-btn-delete smrttasks-btn-secondary-action"
                  title="Delete"
                  data-label="Delete"
                  onClick={() => askDelete(task)}
                >
                  🗑
                </button>
              )}
            </>
          )}

          {section === "waiting" && !isCRMTIX && (
            <>
              <button
                type="button"
                className="smrttasks-btn-icon smrttasks-btn-edit smrttasks-btn-secondary-action"
                title="Edit"
                data-label="Edit"
                onClick={() => openEdit(task)}
              >
                ✎
              </button>
              {canRemark && (
                <button
                  type="button"
                  className="smrttasks-btn-icon smrttasks-btn-remark smrttasks-btn-secondary-action"
                  title="Add a remark"
                  data-label="Remark"
                  onClick={() => openRemarkComposer(task)}
                >
                  💬
                </button>
              )}
              <button
                type="button"
                className="smrttasks-btn-icon smrttasks-btn-delete smrttasks-btn-secondary-action"
                title="Delete"
                data-label="Delete"
                onClick={() => askDelete(task)}
              >
                🗑
              </button>
            </>
          )}

          {section !== "done" && canTogglePrivate && (
            <button
              type="button"
              className={`smrttasks-btn-icon smrttasks-btn-private-row smrttasks-btn-secondary-action${task.isPrivate ? " smrttasks-btn-private-row-active" : ""}`}
              title={task.isPrivate ? "Make public" : "Make private"}
              data-label={task.isPrivate ? "Make public" : "Make private"}
              onClick={() => togglePrivate(task)}
            >
              🔒
            </button>
          )}

          {section === "done" && task.isPrivate && (
            <span
              className="smrttasks-btn-icon smrttasks-btn-private-row smrttasks-btn-private-row-active"
              title="Was private"
              aria-label="Was private"
            >
              🔒
            </span>
          )}

          {section === "done" && canRemark && (
            <button
              type="button"
              className="smrttasks-btn-icon smrttasks-btn-remark smrttasks-btn-secondary-action"
              title="Add a remark"
              data-label="Remark"
              onClick={() => openRemarkComposer(task)}
            >
              💬
            </button>
          )}

          {canReopen && (
            <button
              type="button"
              className="smrttasks-btn-icon smrttasks-btn-warning smrttasks-btn-secondary-action"
              title="Reopen"
              data-label="Reopen"
              onClick={() => askReopen(task)}
            >
              ↺
            </button>
          )}
        </div>
      </div>
    );
  };

  const sortTasks = (tasks: Task[], sort: TabSort): Task[] => {
    const dir = sort.direction === "asc" ? 1 : -1;
    const cmpStr = (a?: string, b?: string): number => {
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    };
    return tasks.slice().sort((a, b) => {
      let cmp = 0;
      switch (sort.column) {
        case "title":
          cmp = cmpStr(a.title, b.title);
          break;
        case "taskType":
          cmp = cmpStr(
            a.taskTypeName || taskTypeName(a.taskTypeId),
            b.taskTypeName || taskTypeName(b.taskTypeId),
          );
          break;
        case "assignedBy":
          cmp = cmpStr(
            a.createdByName || userName(a.createdBy),
            b.createdByName || userName(b.createdBy),
          );
          break;
        case "assignedTo":
          cmp = cmpStr(
            a.assignedUserIds[0]
              ? userName(a.assignedUserIds[0])
              : "",
            b.assignedUserIds[0]
              ? userName(b.assignedUserIds[0])
              : "",
          );
          break;
        case "org":
          cmp = cmpStr(a.orgLabel, b.orgLabel);
          break;
        case "date":
          cmp = cmpStr(a.dueDate, b.dueDate);
          break;
        case "started":
          cmp = cmpStr(a.createdAt, b.createdAt);
          break;
        case "completed":
          cmp = cmpStr(
            a.completedAt || a.rejectedAt,
            b.completedAt || b.rejectedAt,
          );
          break;
      }
      return cmp * dir;
    });
  };

  const currentSort = sortByTab[activeTab];

  const activeTasks = sortTasks(
    activeTab === "mine"
      ? myTasks
      : activeTab === "waiting"
        ? waiting
        : completed,
    currentSort,
  );

  const renderSortArrow = (column: SortColumn) => {
    if (currentSort.column !== column) return null;
    return (
      <span className="smrttasks-sort-arrow">
        {currentSort.direction === "asc" ? " ▲" : " ▼"}
      </span>
    );
  };

  const emptyMessage =
    activeTab === "mine"
      ? "No tasks assigned to you."
      : activeTab === "waiting"
        ? "Nothing waiting on others."
        : "No completed tasks yet.";

  return (
    <div className="smrttasks-page">
      <h1 className="smrttasks-title">SMRT Tasks</h1>

      <div className="smrttasks-tabs">
        <div
          className={`smrttasks-tab${activeTab === "mine" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("mine")}
          title="My Tasks"
        >
          <span className="smrttasks-tab-dot">🔴</span>
          <span className="smrttasks-tab-count">{myTasks.length}</span>
        </div>
        <div
          className={`smrttasks-tab${activeTab === "waiting" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("waiting")}
          title="Waiting on Others"
        >
          <span className="smrttasks-tab-dot">🟡</span>
          <span className="smrttasks-tab-count">{waiting.length}</span>
        </div>
        <div
          className={`smrttasks-tab${activeTab === "done" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("done")}
          title="Completed"
        >
          <span className="smrttasks-tab-dot">🟢</span>
          <span className="smrttasks-tab-count">{completed.length}</span>
        </div>
      </div>

      <div className="smrttasks-card">
        <div className="smrttasks-toolbar">
          <button
            type="button"
            className="smrttasks-btn-primary"
            onClick={openCreate}
          >
            + Add Task
          </button>
        </div>

        {loading && <div className="smrttasks-empty">Loading...</div>}
        {!loading && error && <div className="smrttasks-empty">{error}</div>}

        {!loading && !error && activeTasks.length === 0 && (
          <div className="smrttasks-empty">{emptyMessage}</div>
        )}

        {!loading && !error && activeTasks.length > 0 && (
          <div
            className={`smrttasks-task-header ${headerClassForTab(activeTab)}`}
          >
            <div
              className="smrttasks-task-header-sortable"
              onClick={() => toggleSort("title")}
            >
              Task{renderSortArrow("title")}
            </div>

            <div
              className="smrttasks-task-header-sortable"
              onClick={() => toggleSort("taskType")}
            >
              Task Type{renderSortArrow("taskType")}
            </div>

            {activeTab === "mine" && (
              <>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("assignedBy")}
                >
                  Assigned By{renderSortArrow("assignedBy")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("org")}
                >
                  Organization{renderSortArrow("org")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("date")}
                >
                  Date{renderSortArrow("date")}
                </div>
              </>
            )}

            {activeTab === "waiting" && (
              <>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("assignedTo")}
                >
                  Assigned To{renderSortArrow("assignedTo")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("org")}
                >
                  Organization{renderSortArrow("org")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("date")}
                >
                  Date{renderSortArrow("date")}
                </div>
              </>
            )}

            {activeTab === "done" && (
              <>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("assignedBy")}
                >
                  Created By{renderSortArrow("assignedBy")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("assignedTo")}
                >
                  Assigned To{renderSortArrow("assignedTo")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("org")}
                >
                  Organization{renderSortArrow("org")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("started")}
                >
                  Date Started{renderSortArrow("started")}
                </div>
                <div
                  className="smrttasks-task-header-sortable"
                  onClick={() => toggleSort("completed")}
                >
                  Date Completed{renderSortArrow("completed")}
                </div>
              </>
            )}

            <div className="smrttasks-task-header-actions">Actions</div>
          </div>
        )}

        {!loading &&
          !error &&
          activeTasks.map((t) => renderTaskRow(t, activeTab))}
      </div>

      <SMRTTasksAddTaskModal
        isOpen={modalOpen}
        mode={modalMode}
        initialData={editTaskInitialData}
        canEditAllFields={
          !editingTask || editingTask.createdBy === currentUserID
        }
        creatorUserId={editingTask?.createdBy}
        onClose={() => {
          setModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={handleModalSubmit}
        taskTypes={taskTypes}
        users={users}
      />

      <SMRTTasksRejectModal
        isOpen={rejectTarget !== null}
        taskTitle={rejectTarget?.title}
        onClose={() => setRejectTarget(null)}
        onSubmit={handleReject}
      />

      <SMRTTasksConfirmModal
        isOpen={confirmState !== null}
        title={
          confirmState?.type === "delete"
            ? "Delete Task"
            : confirmState?.type === "complete"
              ? "Complete Task"
              : "Reopen Task"
        }
        message={
          confirmState?.type === "delete"
            ? `Delete "${confirmState.task.title}"? This cannot be undone.`
            : confirmState?.type === "complete"
              ? "This task is part of the CRMTIX workflow and cannot be reopened. Are you sure you want to proceed?"
              : "Are you sure you want to reopen this task?"
        }
        confirmLabel={
          confirmState?.type === "delete"
            ? "Delete"
            : confirmState?.type === "complete"
              ? "Complete"
              : "Reopen"
        }
        variant={confirmState?.type === "delete" ? "danger" : "warning"}
        isLoading={confirmSaving}
        error={confirmError}
        onClose={closeConfirm}
        onConfirm={runConfirm}
      />
    </div>
  );
}
